import {
  ColumnsState,
  UnhideColumns,
} from '@/components/DisplayManagement/types'
import { useColumnManagement } from '@/components/DisplayManagement/useColumnManagement'
import { computeSheetFields } from '@/utils/sheet/sheet-fields'
import { Property } from '@flatfile/api'
import { ColumnConfigProps, EColumnType } from '@flatfile/turntable'
import { createContext, PropsWithChildren, useMemo, useState } from 'react'
import { useRulesContext } from './RulesContext'
import { useSheet } from './SheetContext'

interface FieldsContextType {
  allColumnsHidden: boolean
  columnConfig: ColumnConfigProps[]
  columnsState: ColumnsState
  columnConfigWithState: ColumnConfigProps[]
  hiddenColumnsCount: number
  setColumnsState: (state: ColumnsState) => void
  unhideColumns: UnhideColumns
  overridingConfig: any
  setOverridingConfig: (config: any) => void
  overridingRowCount: number
  setOverridingRowCount: (count: number) => void
  extraKeys: string[]
  setExtraKeys: (keys: string[]) => void
  lockNumber: number
  releaseLocks: () => void
  mentions: any[]
}

const noop = () => {}

export const DEFAULT_FIELDS_CONTEXT = {
  allColumnsHidden: false,
  columnConfig: [],
  columnsState: {},
  columnConfigWithState: [],
  hiddenColumnsCount: 0,
  setColumnsState: noop,
  unhideColumns: noop,
  overridingConfig: null,
  setOverridingConfig: noop,
  overridingRowCount: 1000,
  setOverridingRowCount: noop,
  extraKeys: [],
  setExtraKeys: noop,
  lockNumber: 0,
  releaseLocks: noop,
  mentions: [],
}

export const FieldsContext = createContext<FieldsContextType>(
  DEFAULT_FIELDS_CONTEXT
)

export const FieldsContextProvider = ({ children }: PropsWithChildren) => {
  const [overridingConfig, setOverridingConfig] = useState<any>(null)
  const [overridingRowCount, setOverridingRowCount] = useState(1000)
  const [extraKeys, setExtraKeys] = useState<string[]>([])
  const [lockNumber, setLockNumber] = useState(0)

  const { pendingRules: rules } = useRulesContext()
  const { sheet } = useSheet()

  const { columnConfig: rawColumnConfig } = useMemo(
    () =>
      computeSheetFields(
        ((sheet as any)?.schema?.fields || []) as Property[],
        rules
      ),
    [(sheet as any)?.schema?.fields, rules]
  )

  const columnConfig: ColumnConfigProps[] = (rawColumnConfig || []).map(
    (col, index) => ({
      ...col,
      dataColumnIndex: index,
      type: col.type as EColumnType,
    })
  )

  const columnManagement = useColumnManagement({
    columnConfig,
    sheetId: sheet?.id,
  })

  const releaseLocks = () => {
    setLockNumber((prev) => prev + 1)
  }

  const mentions = useMemo(() => {
    return (
      columnConfig?.map((field) => ({
        ref: `@{{{"type":"field","name":"${field.value}","displayText":"${field.label}","groupName":"Default","groupId":"${sheet?.id}"}}}`,
        displayText: field.label,
        name: field.value,
        type: 'field',
        groupName: 'Default',
        groupId: sheet?.id,
      })) || []
    )
  }, [sheet, columnConfig])

  const value = useMemo(
    () => ({
      columnConfig,
      ...columnManagement,
      overridingConfig,
      setOverridingConfig,
      overridingRowCount,
      setOverridingRowCount,
      extraKeys,
      setExtraKeys,
      lockNumber,
      releaseLocks,
      mentions,
    }),
    [
      columnConfig,
      columnManagement,
      overridingConfig,
      overridingRowCount,
      extraKeys,
      lockNumber,
      mentions,
    ]
  )

  return (
    <FieldsContext.Provider value={value}>{children}</FieldsContext.Provider>
  )
}
